import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { useState } from 'react'
import { EmployeeDepartmentsEditor } from '@/entities/department'
import { type Employee, employeeQueryOptions } from '@/entities/employee'
import { useSession } from '@/entities/session'
import { EditEmployeeDialog } from '@/features/manage-employee'
import { formatDate } from '@/shared/lib'
import { Badge } from '@/shared/ui/badge'
import { Button } from '@/shared/ui/button'
import { Card } from '@/shared/ui/card'
import { PageHeader } from '@/shared/ui/page-header'
import { Spinner } from '@/shared/ui/spinner'
import { StatusScreen } from '@/shared/ui/status-screen'

export const Route = createFileRoute('/_app/employees/$employeeId')({
  loader: ({ context, params }) =>
    context.queryClient.ensureQueryData(employeeQueryOptions(params.employeeId)),
  component: EmployeePage,
})

function EmployeePage() {
  const { employeeId } = Route.useParams()
  const employee = useQuery(employeeQueryOptions(employeeId))

  if (employee.isPending) {
    return (
      <div className="flex justify-center py-16">
        <Spinner />
      </div>
    )
  }
  if (employee.isError || !employee.data) {
    return (
      <StatusScreen title="Сотрудник не найден" message="Ссылка устарела или профиль недоступен.">
        <Link to="/employees">
          <Button variant="secondary">К списку сотрудников</Button>
        </Link>
      </StatusScreen>
    )
  }
  return <EmployeeBody employee={employee.data} />
}

function EmployeeBody({ employee }: { employee: Employee }) {
  const { isAdmin } = useSession()
  const [editOpen, setEditOpen] = useState(false)

  return (
    <>
      <PageHeader
        title={employee.full_name}
        description={employee.email}
        actions={
          isAdmin && (
            <Button variant="secondary" onClick={() => setEditOpen(true)}>
              Изменить
            </Button>
          )
        }
      />

      <div className="mb-6 flex items-center gap-2">
        {employee.is_active ? (
          <Badge tone="success">Активен</Badge>
        ) : (
          <Badge tone="danger">Деактивирован</Badge>
        )}
        {employee.role === 'admin' && <Badge tone="accent">Админ</Badge>}
      </div>

      <Card title="Профиль">
        <dl className="grid grid-cols-[auto_1fr] items-center gap-x-6 gap-y-2 text-sm">
          <Row label="Логин" value={employee.username} />
          <Row label="Email" value={employee.email} />
          <Row label="Роль" value={employee.role || 'employee'} />
          <Row
            label="Telegram"
            value={employee.telegram_linked ? 'Привязан' : 'Не привязан — алерты не дойдут'}
          />
          <Row
            label="Часовой пояс"
            value={employee.timezone || 'Неизвестен'}
            hint="Последняя зона, в которой хаб видел устройство сотрудника"
          />
          <Row label="В хабе с" value={formatDate(employee.created_at) || '—'} />
          <dt className="text-text-secondary">Департаменты</dt>
          <dd>
            <EmployeeDepartmentsEditor employeeId={employee.id} canEdit={isAdmin} />
          </dd>
        </dl>
      </Card>

      {isAdmin && (
        <EditEmployeeDialog
          employee={employee}
          open={editOpen}
          onClose={() => setEditOpen(false)}
        />
      )}
    </>
  )
}

function Row({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <>
      <dt className="text-text-secondary">{label}</dt>
      <dd className="break-words">
        {value}
        {hint && <span className="block text-text-tertiary text-xs">{hint}</span>}
      </dd>
    </>
  )
}
